import React, { useEffect, useMemo, useRef, useState } from 'react'
import ABCJS from 'abcjs'
import {
  buildScoreTimeline,
  parseAbcBpm,
  parseAbcMeasures,
  prepareAbcForStaff,
  stepAtBeat,
  ScoreMeasure,
} from '../../screens/shared/data/scoreData'

const previewDrums: [string, string][] = [
  ['크래쉬심벌', '#f04452'],
  ['탐1', '#ff8a00'],
  ['탐2', '#ffd43b'],
  ['라이드', '#22c55e'],
  ['스네어', '#3182f6'],
  ['하이햇', '#ff5fa2'],
  ['플로어탐', '#8b5cf6'],
  ['킥', '#8b95a1'],
]

interface ScorePreviewModalProps {
  title: string
  artist: string
  level: string
  format: string
  abcText: string
  audioSrc?: string
  onClose: () => void
}

function formatTime(seconds: number) {
  const value = Math.max(0, Math.floor(seconds))
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
}

interface StaffSheetProps {
  abcText: string
  colored: boolean
  measures: ScoreMeasure[]
  currentBeat: number
}

function voiceNoteWindows(measures: ScoreMeasure[], voice: 'hands' | 'feet') {
  const windows: { start: number; end: number }[] = []
  let beat = 0
  measures.forEach((measure) => {
    const positions = [...new Set(measure.events.filter((event) => event.voice === voice).map((event) => event.position))].sort((a, b) => a - b)
    positions.forEach((position) => {
      const events = measure.events.filter((event) => event.voice === voice && event.position === position)
      const duration = events.find((event) => event.durationBeats > 0)?.durationBeats || 0
      const sounding = events.some((event) => !event.rest && Number.isInteger(event.drumIndex))
      if (sounding && duration > 0) windows.push({ start: beat, end: beat + duration })
      beat += duration
    })
  })
  return windows
}

function StaffSheet({ abcText, colored, measures, currentBeat }: StaffSheetProps) {
  const paperRef = useRef<HTMLDivElement | null>(null)
  const [renderError, setRenderError] = useState('')
  const noteWindowsByVoice = useMemo(() => ({
    hands: voiceNoteWindows(measures, 'hands'),
    feet: voiceNoteWindows(measures, 'feet'),
  }), [measures])

  useEffect(() => {
    const paper = paperRef.current
    if (!paper) return

    const source = abcText.trim()
    if (!source) {
      paper.innerHTML = ''
      setRenderError('표시할 ABC 코드가 없습니다.')
      return
    }

    try {
      paper.innerHTML = ''
      ABCJS.renderAbc(paper, prepareAbcForStaff(source), {
        add_classes: true,
        responsive: 'resize',
        staffwidth: 860,
        scale: 0.92,
        paddingtop: 8,
        paddingbottom: 8,
        paddingleft: 0,
        paddingright: 0,
      } as any)
      const voiceOffsets = { hands: 0, feet: 0 }
      ;[...paper.querySelectorAll('.abcjs-note')].forEach((note) => {
        const voice: 'hands' | 'feet' = note.classList.contains('abcjs-v1') ? 'feet' : 'hands'
        const window = noteWindowsByVoice[voice][voiceOffsets[voice]]
        voiceOffsets[voice] += 1
        if (!window) return
        ;(note as HTMLElement).dataset.startBeat = String(window.start)
        ;(note as HTMLElement).dataset.endBeat = String(window.end)
        ;(note as HTMLElement).dataset.scoreVoice = voice
      })
      setRenderError('')
    } catch (error) {
      paper.innerHTML = ''
      setRenderError('악보 데이터를 렌더링하지 못했습니다. ABC 문법을 확인해 주세요.')
    }
  }, [abcText, noteWindowsByVoice])

  useEffect(() => {
    const paper = paperRef.current
    if (!paper) return
    paper.querySelectorAll('.is-playing').forEach((note) => note.classList.remove('is-playing'))
    if (currentBeat < 0) return
    const active = [...paper.querySelectorAll<HTMLElement>('[data-start-beat]')].filter((note) => {
      const start = Number(note.dataset.startBeat)
      const end = Number(note.dataset.endBeat)
      return currentBeat >= start && currentBeat < end
    })
    active.forEach((note) => note.classList.add('is-playing'))
    active[0]?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [currentBeat])

  return (
    <>
      <div className='staff-score'>
        <div ref={paperRef} className={`staff-paper${colored ? ' colorized' : ''}`} />
      </div>
      {renderError ? (
        <p className='sheet-note sheet-note-error'>{renderError}</p>
      ) : (
        <p className='sheet-note'>
          {`입력한 ABC 코드를 abcjs로 실시간 SVG 렌더링한 ${colored ? '컬러' : ''}오선악보 미리보기입니다.`}
        </p>
      )}
    </>
  )
}

function ScorePreviewModal({ title, artist, level, format, abcText, audioSrc, onClose }: ScorePreviewModalProps) {
  const measures = useMemo(() => parseAbcMeasures(abcText), [abcText])
  const timeline = useMemo(() => buildScoreTimeline(measures), [measures])
  const hitSteps = useMemo(() => new Set(
    measures.flatMap((measure) => measure.events
      .filter((event) => !event.rest && Number.isInteger(event.drumIndex))
      .map((event) => measure.startStep + event.position)),
  ), [measures])
  const bpm = parseAbcBpm(abcText)
  const totalBeats = timeline.at(-1) ? timeline.at(-1)!.startBeat + timeline.at(-1)!.durationBeats : 4
  const guideDuration = totalBeats * 60 / Math.max(bpm, 1)

  const [currentBeat, setCurrentBeat] = useState(-1)
  const [playing, setPlaying] = useState(false)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressRef = useRef<HTMLInputElement | null>(null)
  const timeRef = useRef<HTMLSpanElement | null>(null)
  const currentTimeRef = useRef(0)
  const startedAtRef = useRef(0)
  const frameIdRef = useRef(0)
  const lastStepRef = useRef(-1)
  const playingRef = useRef(false)
  const audioContextRef = useRef<AudioContext | null>(null)
  const timelineRef = useRef(timeline)
  const bpmRef = useRef(bpm)
  const durationRef = useRef(guideDuration)
  const hitStepsRef = useRef(hitSteps)
  const audioSrcRef = useRef(audioSrc)

  timelineRef.current = timeline
  bpmRef.current = bpm
  durationRef.current = guideDuration
  hitStepsRef.current = hitSteps
  audioSrcRef.current = audioSrc

  const playGuideTone = (step: number, startBeat: number) => {
    if (audioSrcRef.current || !hitStepsRef.current.has(step)) return
    try {
      const AudioContextClass = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!AudioContextClass) return
      audioContextRef.current ||= new AudioContextClass()
      const audioContext = audioContextRef.current
      if (audioContext.state === 'suspended') audioContext.resume().catch(() => undefined)
      const oscillator = audioContext.createOscillator()
      const gain = audioContext.createGain()
      oscillator.frequency.value = Math.round(startBeat) % 4 === 0 ? 130 : 220
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.07)
      oscillator.connect(gain).connect(audioContext.destination)
      oscillator.start()
      oscillator.stop(audioContext.currentTime + 0.08)
    } catch (error) {
      // Browsers may block sound until the first explicit user interaction.
    }
  }

  const renderPlayback = (resetHighlight = false) => {
    const duration = Math.max(durationRef.current, 0.001)
    if (progressRef.current) progressRef.current.value = String(Math.round(Math.max(currentTimeRef.current, 0) / duration * 1000))
    if (timeRef.current) timeRef.current.textContent = `${formatTime(currentTimeRef.current)} / ${formatTime(duration)}`
    if (resetHighlight) {
      setCurrentBeat((previous) => (previous === -1 ? previous : -1))
      return -1
    }
    const beat = currentTimeRef.current * bpmRef.current / 60
    const step = stepAtBeat(timelineRef.current, beat)
    const startBeat = timelineRef.current.find((entry) => entry.step === step)?.startBeat ?? -1
    setCurrentBeat((previous) => (previous === startBeat ? previous : startBeat))
    return step
  }

  const stopPlayback = () => {
    playingRef.current = false
    setPlaying(false)
    cancelAnimationFrame(frameIdRef.current)
    audioRef.current?.pause()
    if (audioRef.current) audioRef.current.currentTime = 0
    currentTimeRef.current = 0
    lastStepRef.current = -1
    renderPlayback(true)
  }

  useEffect(() => () => {
    playingRef.current = false
    cancelAnimationFrame(frameIdRef.current)
    audioContextRef.current?.close().catch(() => undefined)
  }, [])

  useEffect(() => {
    renderPlayback(true)
    // Reset the playhead when the sheet (and its beat timeline) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcText])

  const tick = () => {
    if (!playingRef.current) return
    const duration = durationRef.current
    currentTimeRef.current = Math.min((performance.now() - startedAtRef.current) / 1000, duration)
    const nextStep = renderPlayback()
    if (nextStep !== lastStepRef.current) {
      const item = timelineRef.current.find((entry) => entry.step === nextStep)
      lastStepRef.current = nextStep
      if (item) playGuideTone(nextStep, item.startBeat)
    }
    if (currentTimeRef.current >= duration) {
      stopPlayback()
      return
    }
    frameIdRef.current = requestAnimationFrame(tick)
  }

  const startPlayback = async () => {
    if (playingRef.current) return
    playingRef.current = true
    setPlaying(true)
    const audio = audioRef.current
    if (audioSrc && audio) {
      audio.currentTime = currentTimeRef.current
      try {
        await audio.play()
      } catch (error) {
        playingRef.current = false
        setPlaying(false)
        return
      }
    }
    startedAtRef.current = performance.now() - currentTimeRef.current * 1000
    try {
      if (audioContextRef.current?.state === 'suspended') await audioContextRef.current.resume()
    } catch (error) {
      // Ignore autoplay restrictions on the metronome tone.
    }
    frameIdRef.current = requestAnimationFrame(tick)
  }

  const pausePlayback = () => {
    if (!playingRef.current) return
    playingRef.current = false
    setPlaying(false)
    cancelAnimationFrame(frameIdRef.current)
    currentTimeRef.current = Math.min((performance.now() - startedAtRef.current) / 1000, durationRef.current)
    audioRef.current?.pause()
    renderPlayback()
  }

  const handleProgressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const duration = durationRef.current
    currentTimeRef.current = duration * Number(event.target.value) / 1000
    const audio = audioRef.current
    if (audioSrc && audio && Number.isFinite(audio.duration)) audio.currentTime = currentTimeRef.current
    if (playingRef.current) startedAtRef.current = performance.now() - currentTimeRef.current * 1000
    renderPlayback()
  }

  const isColoredStaff = format !== '오선악보'

  return (
    <div className='score-preview-backdrop' role='presentation' onClick={onClose}>
      <section className='score-preview-window' role='dialog' aria-modal='true' onClick={(event) => event.stopPropagation()}>
        <div className='preview-window-top'>
          <div>
            <strong>{title || '제목 미입력'}</strong>
            <small>{`${artist || '가수 미입력'} · ${level} · ${format}`}</small>
          </div>
          <button className='secondary-button' type='button' onClick={onClose}>닫기</button>
        </div>

        <div className='playback-bar' aria-label='악보 재생 제어'>
          <button className='primary' type='button' onClick={startPlayback}>{playing ? '재생 중' : '재생'}</button>
          <button type='button' onClick={pausePlayback}>일시정지</button>
          <button type='button' onClick={stopPlayback}>정지</button>
          <input ref={progressRef} type='range' min={0} max={1000} defaultValue={0} aria-label='재생 위치' onChange={handleProgressChange} />
          <span className='playback-time' ref={timeRef}>{`${formatTime(0)} / ${formatTime(guideDuration)}`}</span>
          {audioSrc ? <audio ref={audioRef} src={audioSrc} preload='auto' /> : null}
        </div>

        <div className='sheet'>
          {isColoredStaff ? (
            <div className='score-visual-legend'>
              {previewDrums.map(([name, color]) => (
                <span key={name}><i style={{ '--c': color } as React.CSSProperties} />{name}</span>
              ))}
            </div>
          ) : null}
          <StaffSheet abcText={abcText} colored={isColoredStaff} measures={measures} currentBeat={currentBeat} />
        </div>
      </section>
    </div>
  )
}

export default ScorePreviewModal
